import React, { useState, useEffect } from 'react';
import axios from 'axios';

const orderStatusLabels = {
  pending: 'Bekliyor', confirmed: 'Onaylandı', processing: 'Hazırlanıyor', shipped: 'Kargoda',
  delivered: 'Teslim', cancelled: 'İptal', returned: 'İade'
};
const orderStatusColors = {
  pending: '#F59E0B', confirmed: '#3B82F6', processing: '#8B5CF6', shipped: '#14B8A6',
  delivered: '#10B981', cancelled: '#EF4444', returned: '#6B7280'
};
const payStatusLabels = { pending: 'Bekliyor', paid: 'Ödendi', refunded: 'İade', partially_refunded: 'Kısmi İade' };

function EcommerceOrders() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [modal, setModal] = useState({ open: false, order: null });
  const [form, setForm] = useState({});
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [newCustomerMode, setNewCustomerMode] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ first_name: '', last_name: '', phone: '' });
  const token = localStorage.getItem('crm_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); }, [filter]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      const [o, s] = await Promise.all([
        axios.get('/api/ecommerce/orders', { headers, params }),
        axios.get('/api/ecommerce/stats', { headers })
      ]);
      setOrders(o.data);
      setStats(s.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const updateStatus = async (id, status, extra = {}) => {
    await axios.put(`/api/ecommerce/orders/${id}/status`, { status, ...extra }, { headers });
    fetchAll();
  };

  const updatePayment = async (id, payment_status) => {
    await axios.put(`/api/ecommerce/orders/${id}/payment`, { payment_status }, { headers });
    fetchAll();
  };

  const searchCustomers = async (q) => {
    setCustomerQuery(q);
    if (!q.trim()) { setCustomerResults([]); return; }
    try {
      const res = await axios.get(`/api/customers?search=${encodeURIComponent(q)}&limit=8`, { headers });
      setCustomerResults(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) { console.error(e); }
  };

  const selectCustomerForOrder = (c) => {
    setForm({ ...form, customer_id: c.id });
    setCustomerQuery(`${c.first_name} ${c.last_name || ''}`.trim());
    setCustomerResults([]);
  };

  const createCustomerForOrder = async () => {
    if (!newCustomer.first_name.trim() || !newCustomer.phone.trim()) {
      alert('Ad ve telefon zorunludur'); return;
    }
    try {
      const res = await axios.post('/api/customers', newCustomer, { headers });
      selectCustomerForOrder(res.data);
      setNewCustomerMode(false);
      setNewCustomer({ first_name: '', last_name: '', phone: '' });
    } catch (e) { alert(e.response?.data?.error || 'Müşteri oluşturulamadı'); }
  };

  const saveOrder = async () => {
    if (!form.customer_id) {
      alert('Lütfen önce bir müşteri seçin veya yeni müşteri ekleyin.');
      return;
    }
    try {
      await axios.post('/api/ecommerce/orders', { ...form, items: JSON.parse(form.items_raw || '[]') }, { headers });
      setModal({ open: false }); setForm({}); setCustomerQuery(''); setCustomerResults([]); setNewCustomerMode(false);
      fetchAll();
    } catch (e) { alert(e.response?.data?.error || 'Hata'); }
  };

  const viewOrder = async (id) => {
    const res = await axios.get(`/api/ecommerce/orders/${id}`, { headers });
    setModal({ open: true, order: res.data });
  };

  if (loading) return <div className="loading"><div className="loading-spinner"></div></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">🛒 Sipariş Yönetimi</h1>
          <p className="page-subtitle">E-ticaret sipariş takibi</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm({}); setCustomerQuery(''); setCustomerResults([]); setNewCustomerMode(false); setModal({ open: true, order: null }); }}>+ Yeni Sipariş</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px' }}>
        {[
          { label: 'Toplam Sipariş', value: stats.totalOrders, icon: '📦', color: '#3B82F6' },
          { label: 'Gelir', value: `${Number(stats.totalRevenue || 0).toLocaleString('tr-TR')} ₺`, icon: '💰', color: '#10B981' },
          { label: 'Bekleyen', value: stats.pendingOrders, icon: '⏳', color: '#F59E0B' },
          { label: 'İade', value: stats.totalReturns, icon: '↩️', color: '#EF4444' }
        ].map((s, i) => (
          <div key={i} className="card" style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: s.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>{s.icon}</div>
            <div>
              <div style={{ fontSize: '22px', fontWeight: '700', color: s.color }}>{s.value || 0}</div>
              <div style={{ fontSize: '13px', color: '#64748b' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
        <button className={`btn btn-sm ${!filter ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter('')}>Tümü</button>
        {Object.entries(orderStatusLabels).map(([k, v]) => (
          <button key={k} className={`btn btn-sm ${filter === k ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter(k)}>{v}</button>
        ))}
      </div>

      {/* Orders table */}
      <div className="card" style={{ marginTop: '16px' }}>
        <div className="table-container">
          <table className="table">
            <thead><tr><th>Sipariş No</th><th>Müşteri</th><th>Tutar</th><th>Ödeme</th><th>Kargo</th><th>Durum</th><th>İşlem</th></tr></thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id}>
                  <td>
                    <span className="font-medium" style={{ cursor: 'pointer', color: '#3B82F6' }} onClick={() => viewOrder(o.id)}>{o.order_number}</span>
                    <div className="text-xs text-muted">{new Date(o.created_at).toLocaleDateString('tr-TR')}</div>
                  </td>
                  <td>
                    <div className="font-medium">{o.first_name} {o.last_name}</div>
                    <div className="text-xs text-muted">{o.phone}</div>
                  </td>
                  <td className="font-medium">{Number(o.total_amount).toLocaleString('tr-TR')} ₺</td>
                  <td>
                    <span className="badge" style={{ background: o.payment_status === 'paid' ? '#10B98120' : '#F59E0B20', color: o.payment_status === 'paid' ? '#10B981' : '#F59E0B' }}>
                      {payStatusLabels[o.payment_status]}
                    </span>
                  </td>
                  <td>{o.tracking_number ? <span style={{ fontSize: '13px' }}>📦 {o.tracking_number}</span> : '-'}</td>
                  <td>
                    <span className="badge" style={{ background: orderStatusColors[o.status] + '20', color: orderStatusColors[o.status] }}>
                      {orderStatusLabels[o.status]}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {o.status === 'confirmed' && <button className="btn btn-sm btn-ghost" onClick={() => updateStatus(o.id, 'processing')}>Hazırla</button>}
                      {o.status === 'processing' && <button className="btn btn-sm btn-primary" onClick={() => {
                        const tracking = prompt('Kargo takip no:');
                        if (tracking) updateStatus(o.id, 'shipped', { tracking_number: tracking, carrier: 'Kargo' });
                      }}>Kargola</button>}
                      {o.status === 'shipped' && <button className="btn btn-sm btn-primary" onClick={() => updateStatus(o.id, 'delivered')}>Teslim</button>}
                      {o.payment_status === 'pending' && <button className="btn btn-sm btn-ghost" onClick={() => updatePayment(o.id, 'paid')}>💳 Ödendi</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan="7" className="text-center text-muted" style={{ padding: '40px' }}>Sipariş bulunamadı</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Detail / New Order Modal */}
      {modal.open && (
        <div className="modal-overlay" onClick={() => setModal({ open: false })}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: modal.order ? '700px' : '500px' }}>
            <div className="modal-header">
              <h2>{modal.order ? `Sipariş: ${modal.order.order_number}` : 'Yeni Sipariş'}</h2>
              <button className="modal-close" onClick={() => setModal({ open: false })}>×</button>
            </div>
            <div className="modal-body">
              {modal.order ? (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div><span style={{ color: '#64748b' }}>Müşteri:</span> <strong>{modal.order.first_name} {modal.order.last_name}</strong></div>
                    <div><span style={{ color: '#64748b' }}>Telefon:</span> {modal.order.phone}</div>
                    <div><span style={{ color: '#64748b' }}>Durum:</span> <span className="badge" style={{ background: orderStatusColors[modal.order.status] + '20', color: orderStatusColors[modal.order.status] }}>{orderStatusLabels[modal.order.status]}</span></div>
                    <div><span style={{ color: '#64748b' }}>Ödeme:</span> {payStatusLabels[modal.order.payment_status]}</div>
                    <div><span style={{ color: '#64748b' }}>Tutar:</span> <strong>{Number(modal.order.total_amount).toLocaleString('tr-TR')} ₺</strong></div>
                    <div><span style={{ color: '#64748b' }}>Kargo:</span> {modal.order.tracking_number || '-'}</div>
                  </div>
                  {modal.order.shipping_address && <div style={{ padding: '12px', background: 'var(--gray-50, #f8fafc)', borderRadius: '8px', marginBottom: '12px' }}>
                    <strong>Teslimat Adresi:</strong> {modal.order.shipping_address}
                  </div>}
                  {modal.order.returns?.length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                      <h4>İade Talepleri</h4>
                      {modal.order.returns.map(r => (
                        <div key={r.id} style={{ padding: '12px', background: '#FEF2F2', borderRadius: '8px', marginTop: '8px' }}>
                          <strong>{r.return_number}</strong> — {r.reason} — {r.status}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Müşteri *</label>
                    {form.customer_id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: 'var(--gray-100, #f1f5f9)', borderRadius: '8px' }}>
                        <span style={{ fontWeight: 500, flex: 1 }}>{customerQuery}</span>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setForm({ ...form, customer_id: undefined }); setCustomerQuery(''); }}>Değiştir</button>
                      </div>
                    ) : !newCustomerMode ? (
                      <>
                        <input className="input" placeholder="İsim veya telefon ile ara..." value={customerQuery} onChange={e => searchCustomers(e.target.value)} />
                        {customerResults.length > 0 && (
                          <div style={{ border: '1px solid var(--border, #e2e8f0)', borderRadius: '8px', marginTop: '4px', maxHeight: '160px', overflowY: 'auto' }}>
                            {customerResults.map(c => (
                              <div key={c.id} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border, #e2e8f0)' }}
                                onClick={() => selectCustomerForOrder(c)}>
                                <div style={{ fontWeight: 500 }}>{c.first_name} {c.last_name}</div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>{c.phone}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: '6px' }} onClick={() => setNewCustomerMode(true)}>+ Yeni Müşteri Ekle</button>
                      </>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', border: '1px solid var(--border, #e2e8f0)', borderRadius: '8px' }}>
                        <input className="input" placeholder="Ad" value={newCustomer.first_name} onChange={e => setNewCustomer({ ...newCustomer, first_name: e.target.value })} />
                        <input className="input" placeholder="Soyad" value={newCustomer.last_name} onChange={e => setNewCustomer({ ...newCustomer, last_name: e.target.value })} />
                        <input className="input" placeholder="Telefon" value={newCustomer.phone} onChange={e => setNewCustomer({ ...newCustomer, phone: e.target.value })} />
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setNewCustomerMode(false)}>Vazgeç</button>
                          <button type="button" className="btn btn-primary btn-sm" onClick={createCustomerForOrder}>Müşteriyi Oluştur</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="form-group"><label className="form-label">Ürünler (JSON)</label>
                    <textarea className="textarea" rows={3} value={form.items_raw || '[]'} onChange={e => setForm({ ...form, items_raw: e.target.value })}
                      placeholder='[{"name":"Ürün","qty":1,"price":100}]' />
                  </div>
                  <div className="form-group"><label className="form-label">Toplam (₺)</label>
                    <input type="number" className="input" value={form.total_amount || ''} onChange={e => setForm({ ...form, total_amount: e.target.value, subtotal: e.target.value })} />
                  </div>
                  <div className="form-group"><label className="form-label">Teslimat Adresi</label>
                    <textarea className="textarea" value={form.shipping_address || ''} onChange={e => setForm({ ...form, shipping_address: e.target.value })} />
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal({ open: false })}>Kapat</button>
              {!modal.order && <button className="btn btn-primary" onClick={saveOrder}>Sipariş Oluştur</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EcommerceOrders;
